import { apiUrl } from "@/utils/api";
import {
  chatState,
  getAuthHeaders,
  ensureChatInitialized,
} from "@/stores/chat";

// Helper to safely get JSON body
async function safeJson(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}

function qs(params) {
  const u = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.append(k, v);
  });
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const ChatService = {
  async uploadFile({
    file,
    filename,
    subtype,
    width,
    height,
    duration,
    onProgress,
  }) {
    await ensureChatInitialized();
    const form = new FormData();
    form.append("file", file, filename || file.name || "file");
    form.append("filename", filename || file.name || "file");
    // TalkJS only accepts these subtypes: 'image', 'video', 'audio', 'voice'
    // For documents (PDF, Word, etc.), don't send subtype
    const validSubtypes = ["image", "video", "audio", "voice"];
    if (subtype && validSubtypes.includes(subtype)) {
      form.append("subtype", subtype);
    }
    if (width != null) form.append("width", String(width));
    if (height != null) form.append("height", String(height));
    if (duration != null) form.append("duration", String(duration));

    // Use XMLHttpRequest for upload progress tracking
    if (onProgress && typeof onProgress === "function") {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const json = JSON.parse(xhr.responseText);
              // Try multiple possible response structures
              const token =
                json &&
                (json.attachmentToken ||
                  json.fileToken ||
                  json.token ||
                  json.data?.attachmentToken ||
                  json.data?.fileToken);
              if (!token) {
                console.warn(
                  "[ChatService] Upload succeeded but no token in response:",
                  json
                );
              }
              resolve(token);
            } catch (e) {
              console.error(
                "[ChatService] Failed to parse upload response:",
                e,
                xhr.responseText
              );
              reject(new Error("Failed to parse upload response"));
            }
          } else {
            console.error(
              "[ChatService] Upload failed with status:",
              xhr.status,
              xhr.statusText
            );
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });

        xhr.addEventListener("error", (e) => {
          console.error("[ChatService] Upload network error:", e);
          reject(new Error("Network error during upload"));
        });
        xhr.addEventListener("abort", () => {
          console.warn("[ChatService] Upload aborted");
          reject(new Error("Upload aborted"));
        });

        xhr.open("POST", `${apiUrl}/chat/files`);
        const headers = getAuthHeaders();
        Object.keys(headers).forEach((key) => {
          if (key.toLowerCase() === "content-type") return; // Let browser set multipart boundary
          xhr.setRequestHeader(key, headers[key]);
        });
        xhr.send(form);
      });
    }

    // Fallback to fetch without progress
    // Remove Content-Type header for FormData - browser will set multipart/form-data with boundary
    const headers = getAuthHeaders();
    delete headers["Content-Type"];
    const res = await fetch(`${apiUrl}/chat/files`, {
      method: "POST",
      headers,
      body: form,
    });
    if (!res.ok) return null;
    const json = await safeJson(res);
    return json && (json.attachmentToken || json.fileToken || json.token);
  },
  // Conversations
  async getConversations() {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/conversations`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await safeJson(res);
    const list = (json && (json.data || json)) || [];
    return Array.isArray(list) ? list : [];
  },

  // All conversations including deleted ones (Flutter sync: ChatEndpoints.allConversations)
  async getAllConversations({ search } = {}) {
    await ensureChatInitialized();
    const params = new URLSearchParams({ include_deleted: '1' });
    if (search && search.trim()) params.append('search', search.trim());
    const res = await fetch(`${apiUrl}/chat/conversations?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await safeJson(res);
    const list = (json && (json.data || json)) || [];
    return Array.isArray(list) ? list : [];
  },

  // Delete a conversation (Flutter sync: ChatEndpoints.deleteConversation)
  async deleteConversation(conversationId) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) return { success: false };
    const json = await safeJson(res);
    return { success: true, data: json };
  },

  async getConversation(conversationId) {
    await ensureChatInitialized();
    
    // Helper to check if conversation has a valid name
    const hasValidName = (conv) => {
      if (!conv) return false;
      return !!(conv.subject || conv.name || conv.title || 
                (conv.custom && (conv.custom.name || conv.custom.title)) ||
                (conv.participantNames && Object.values(conv.participantNames).length > 0) ||
                (conv.participants && conv.participants.length > 0 && conv.participants.some(p => p.name)));
    };
    
    // Helper to find conversation by ID
    const findConversation = (list) => {
      return list.find(c => 
        String(c.id) === String(conversationId) || 
        String(c.talkjs_conversation_id) === String(conversationId)
      );
    };
    
    // Fetch both endpoints in parallel for better performance
    const [conversations, allConversations] = await Promise.all([
      this.getConversations(),
      this.getAllConversations()
    ]);
    
    const fromList = findConversation(conversations);
    const fromAllList = findConversation(allConversations);
    
    // Prefer the one with a valid name, otherwise use fromList first
    if (fromList && hasValidName(fromList)) {
      return fromList;
    }
    if (fromAllList && hasValidName(fromAllList)) {
      return fromAllList;
    }
    // If neither has valid name, prefer fromList (active) over fromAllList
    if (fromList) {
      return fromList;
    }
    if (fromAllList) {
      return fromAllList;
    }
    
    // Fallback to single conversation endpoint
    const res = await fetch(`${apiUrl}/chat/conversations/${conversationId}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return null;
    const json = await safeJson(res);
    return json && (json.data || json);
  },

  // Messages
  async getMessages({ conversationId, limit = 50, afterMessageId }) {
    await ensureChatInitialized();
    const query = qs({ limit, startAfter: afterMessageId });
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages${query}`,
      { headers: getAuthHeaders() }
    );
    if (!res.ok) return [];
    const json = await safeJson(res);
    const list = (json && (json.data || json)) || [];
    return Array.isArray(list) ? list : [];
  },

  async getMessage({ conversationId, messageId }) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}`,
      { headers: getAuthHeaders() }
    );
    if (!res.ok) return null;
    return await safeJson(res);
  },

  /**
   * Send a message and return the confirmed message from the server.
   * Matches Flutter's TalkJSRestService.sendMessage pattern exactly.
   * 
   * @param {Object} params
   * @param {string} params.conversationId
   * @param {string} [params.text]
   * @param {string} [params.fileToken] - File token from uploadFile/uploadFileWithCancel
   * @param {string} [params.subtype] - 'image', 'video', 'audio', 'voice'
   * @param {Object} [params.custom] - Custom metadata
   * @param {number} [params.durationSeconds] - Voice duration
   * @returns {Promise<Object|null>} The confirmed message from server, or null on failure
   */
  async sendMessage({
    conversationId,
    text,
    fileToken,
    subtype,
    custom,
    durationSeconds,
  }) {
    await ensureChatInitialized();
    const headers = getAuthHeaders();
    
    // Determine message_type based on content (matches Flutter exactly)
    let messageType;
    if (fileToken) {
      if (subtype === "voice") messageType = "audio";
      else if (subtype === "image" || subtype === "video")
        messageType = "media";
      else messageType = "attachment";
    } else if (text && text.length > 0) {
      messageType = "text";
    } else {
      return null;
    }

    // Build message object (matches Flutter's messageData structure)
    const messageData = {
      sender: chatState.userId,
      type: "UserMessage",
      custom: {
        message_type: messageType,
        ...(durationSeconds ? { voice_duration: String(durationSeconds) } : {}),
        ...(custom || {}),
      },
    };

    // Add text or file content (matches Flutter exactly)
    if (fileToken) {
      messageData.content = [{ type: "file", fileToken }];
    } else if (text && text.length > 0) {
      messageData.text = text;
    } else {
      return null;
    }

    try {
      // TalkJS API expects an ARRAY of messages (matches Flutter)
      const response = await fetch(
        `${apiUrl}/chat/conversations/${conversationId}/messages`,
        {
          method: "POST",
          headers,
          body: JSON.stringify([messageData]),
        }
      );

      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData?.message || `HTTP ${response.status}`);
      }

      // Response is an array of objects with IDs: [{"id": "msg_abc"}]
      const responseData = await safeJson(response);
      const messageId = responseData?.[0]?.id;

      if (!messageId) {
        console.warn("[ChatService] sendMessage: No message ID in response");
        return null;
      }

      // Fetch full message details (matches Flutter's getMessage call after POST)
      const messageResponse = await fetch(
        `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}`,
        { headers: getAuthHeaders() }
      );

      if (!messageResponse.ok) {
        console.warn("[ChatService] sendMessage: Failed to fetch message details");
        return null;
      }

      const confirmedMessage = await safeJson(messageResponse);
      console.log("[ChatService] Message sent and confirmed:", messageId);
      return confirmedMessage;
    } catch (err) {
      console.error("[ChatService] sendMessage error:", err);
      throw err;
    }
  },

  /**
   * Send a reply message and return the confirmed message from the server.
   * Matches Flutter's TalkJSRestService.sendReply pattern exactly.
   * 
   * @param {Object} params
   * @param {string} params.conversationId
   * @param {string} params.referencedMessageId - ID of message being replied to
   * @param {string} [params.text]
   * @param {string} [params.fileToken] - File token from uploadFile/uploadFileWithCancel
   * @param {string} [params.subtype] - 'image', 'video', 'audio', 'voice'
   * @param {Object} [params.custom] - Custom metadata
   * @param {number} [params.durationSeconds] - Voice duration
   * @returns {Promise<Object|null>} The confirmed message from server, or null on failure
   */
  async sendReply({
    conversationId,
    text,
    referencedMessageId,
    fileToken,
    subtype,
    custom,
    durationSeconds,
  }) {
    await ensureChatInitialized();
    const headers = getAuthHeaders();
    
    // Determine message_type based on content (matches Flutter exactly)
    let messageType;
    if (fileToken) {
      if (subtype === "voice") messageType = "audio";
      else if (subtype === "image" || subtype === "video")
        messageType = "media";
      else messageType = "attachment";
    } else if (text && text.length > 0) {
      messageType = "text";
    } else {
      return null;
    }

    // Build message object with reply reference (matches Flutter's messageData structure)
    const messageData = {
      sender: chatState.userId,
      type: "UserMessage",
      referencedMessageId,
      custom: {
        message_type: messageType,
        ...(durationSeconds ? { voice_duration: String(durationSeconds) } : {}),
        ...(custom || {}),
      },
    };

    // Add text or file content (matches Flutter exactly)
    if (fileToken) {
      messageData.content = [{ type: "file", fileToken }];
    } else if (text && text.length > 0) {
      messageData.text = text;
    } else {
      return null;
    }

    try {
      // TalkJS API expects an ARRAY of messages (matches Flutter)
      const response = await fetch(
        `${apiUrl}/chat/conversations/${conversationId}/messages`,
        {
          method: "POST",
          headers,
          body: JSON.stringify([messageData]),
        }
      );

      if (!response.ok) {
        const errorData = await safeJson(response);
        throw new Error(errorData?.message || `HTTP ${response.status}`);
      }

      // Response is an array of objects with IDs: [{"id": "msg_abc"}]
      const responseData = await safeJson(response);
      const messageId = responseData?.[0]?.id;

      if (!messageId) {
        console.warn("[ChatService] sendReply: No message ID in response");
        return null;
      }

      // Fetch full message details (matches Flutter's getMessage call after POST)
      const messageResponse = await fetch(
        `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}`,
        { headers: getAuthHeaders() }
      );

      if (!messageResponse.ok) {
        console.warn("[ChatService] sendReply: Failed to fetch message details");
        return null;
      }

      const confirmedMessage = await safeJson(messageResponse);
      console.log("[ChatService] Reply sent and confirmed:", messageId);
      return confirmedMessage;
    } catch (err) {
      console.error("[ChatService] sendReply error:", err);
      throw err;
    }
  },

  async markAsRead({ conversationId }) {
    await ensureChatInitialized();
    await fetch(`${apiUrl}/chat/conversations/${conversationId}/read`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    }).catch(() => {});
    return true;
  },

  async editMessage({ conversationId, messageId, text, markEdited = true }) {
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}`,
      {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ text, markEdited }),
      }
    );
    return res.ok;
  },

  async deleteMessage({ conversationId, messageId }) {
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}`,
      { method: "DELETE", headers: getAuthHeaders() }
    );
    return res.ok;
  },

  async addReaction({ conversationId, messageId, emoji, userId }) {
    const encoded = encodeURIComponent(emoji);
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}/reactions/${encoded}/${
        userId || chatState.userId
      }`,
      { method: "PUT", headers: getAuthHeaders(), body: JSON.stringify({}) }
    );
    return res.ok;
  },

  async removeReaction({ conversationId, messageId, emoji, userId }) {
    const encoded = encodeURIComponent(emoji);
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages/${messageId}/reactions/${encoded}/${
        userId || chatState.userId
      }`,
      { method: "DELETE", headers: getAuthHeaders() }
    );
    return res.ok;
  },

  async checkConversation(userId) {
    const res = await fetch(`${apiUrl}/chat/check-conversation/${userId}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return null;
    return await safeJson(res);
  },

  async leaveConversation(conversationId) {
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/leave`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      }
    );
    if (!res.ok) return { success: false };
    return await safeJson(res);
  },

  // ==================== QR Code & Shareable ID ====================

  /**
   * Get the current user's shareable ID for QR code generation
   * GET /api/chat/shareable-id
   */
  async getShareableId() {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/shareable-id`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return null;
    const json = await safeJson(res);
    console.log("[ChatService] getShareableId response:", json);

    // Handle multiple possible response formats
    if (!json) return null;

    // Try different possible paths for the shareable_id
    const id =
      json.data?.shareable_id ||
      json.data?.shareableId ||
      json.shareable_id ||
      json.shareableId ||
      json.data?.id ||
      json.id ||
      (typeof json.data === "string" ? json.data : null) ||
      (typeof json === "string" ? json : null);

    console.log("[ChatService] Extracted shareable ID:", id);
    return id;
  },

  // ==================== Message Requests ====================

  /**
   * Get received message requests
   * GET /api/chat/requests/received?status=pending
   */
  async getReceivedRequests(status = "pending") {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/requests/received?status=${status}`,
      {
        headers: getAuthHeaders(),
      }
    );
    if (!res.ok) return [];
    const json = await safeJson(res);
    return (json && json.data) || [];
  },

  /**
   * Get sent message requests
   * GET /api/chat/requests/sent?status=pending
   */
  async getSentRequests(status = "pending") {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/sent?status=${status}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await safeJson(res);
    return (json && json.data) || [];
  },

  /**
   * Get pending requests count
   * GET /api/chat/requests/pending/count
   */
  async getPendingRequestsCount() {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/pending/count`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return 0;
    const json = await safeJson(res);
    return (json && json.data && json.data.pending_count) || 0;
  },

  /**
   * Accept a message request
   * POST /api/chat/requests/{id}/accept
   */
  async acceptRequest(requestId) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/${requestId}/accept`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) return { success: false };
    const json = await safeJson(res);
    return json || { success: true };
  },

  /**
   * Reject a message request
   * POST /api/chat/requests/{id}/reject
   */
  async rejectRequest(requestId) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/${requestId}/reject`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) return { success: false };
    const json = await safeJson(res);
    return json || { success: true };
  },

  /**
   * Delete a sent request
   * DELETE /api/chat/requests/{id}
   */
  async deleteRequest(requestId) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/${requestId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to delete request",
      };
    }
    return { success: true, ...json };
  },

  // ==================== Add Friend ====================

  /**
   * Send friend request via shareable ID
   * POST /api/chat/requests/via-shareable-id
   */
  async sendRequestViaShareableId(shareableId, message = null) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/via-shareable-id`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        shareable_id: shareableId,
        message: message || undefined,
      }),
    });
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to send request",
        status: res.status,
      };
    }
    return { success: true, data: json?.data };
  },

  /**
   * Send friend request via user ID
   * POST /api/chat/requests/via-user-id
   */
  async sendRequestViaUserId(userId, message = null) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/requests/via-user-id`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        user_id: userId,
        message: message || undefined,
      }),
    });
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to send request",
        status: res.status,
      };
    }
    return { success: true, data: json?.data };
  },

  // ==================== Group Chat ====================

  /**
   * Create a group chat
   * POST /api/chat/groups (multipart/form-data)
   */
  async createGroupChat({ name, participantIds, photo = null }) {
    await ensureChatInitialized();
    const form = new FormData();
    form.append("name", name);

    // Add participant IDs as array (ensure they're integers)
    participantIds.forEach((id) => {
      // Convert to integer - API expects integer IDs
      const intId = parseInt(id, 10);
      if (!isNaN(intId)) {
        form.append("participant_ids[]", intId);
      }
    });

    if (photo) {
      form.append("photo", photo);
    }

    // Remove Content-Type for multipart/form-data
    const headers = getAuthHeaders();
    delete headers["Content-Type"];

    const res = await fetch(`${apiUrl}/chat/groups`, {
      method: "POST",
      headers,
      body: form,
    });

    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to create group",
        status: res.status,
      };
    }
    return { success: true, data: json?.data };
  },

  /**
   * Get group details
   * GET /api/chat/groups/{id}
   */
  async getGroupDetails(groupId) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/groups/${groupId}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return null;
    const json = await safeJson(res);
    return json && json.data ? json.data : null;
  },

  /**
   * Get direct conversations for group participant selection
   * Returns only 1:1 conversations (not groups) with parsed participant details
   */
  async getDirectConversations() {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/conversations`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await safeJson(res);
    console.log("[ChatService] getDirectConversations response:", json);

    const list = (json && (json.data || json)) || [];
    if (!Array.isArray(list)) return [];

    // Filter to only include direct conversations (type === 'direct')
    const directChats = list.filter((c) => c.custom?.type === "direct");
    console.log("[ChatService] Filtered direct chats:", directChats.length);

    // Parse participants_details for each conversation
    return directChats.map((conv) => {
      let participantsDetails = [];
      try {
        if (conv.custom?.participants_details) {
          participantsDetails = JSON.parse(conv.custom.participants_details);
        }
      } catch (e) {
        console.warn("[ChatService] Failed to parse participants_details:", e);
      }

      return {
        ...conv,
        parsedParticipants: participantsDetails,
        // Get participant IDs from the participants object keys
        participantIds: conv.participants ? Object.keys(conv.participants) : [],
      };
    });
  },

  // ==================== Block/Unblock (Flutter Sync) ====================

  /**
   * Block user in a DM conversation
   * POST /api/chat/conversations/{id}/block
   * Like Flutter's TalkJSRestService.blockUser
   */
  async blockUser(conversationId) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/block`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to block user",
      };
    }
    return { success: true, ...json };
  },

  /**
   * Unblock user in a DM conversation
   * POST /api/chat/conversations/{id}/unblock
   * Like Flutter's TalkJSRestService.unblockUser
   */
  async unblockUser(conversationId) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/unblock`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to unblock user",
      };
    }
    return { success: true, ...json };
  },

  // ==================== Group Moderation (Flutter Sync) ====================

  /**
   * Toggle chat lock for group (owner only)
   * POST /api/chat/conversations/{id}/toggle-chat
   * Like Flutter's toggleGroupChat - sends { enabled: true/false }
   * @param {string} conversationId - The conversation ID
   * @param {boolean} enabled - true to unlock chat, false to lock chat
   */
  async toggleChatLock(conversationId, enabled = true) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/toggle-chat`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled }),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to toggle chat lock",
      };
    }
    return { success: true, is_locked: json?.is_locked, ...json };
  },

  /**
   * Kick user from group (owner only)
   * POST /api/chat/conversations/{id}/kick
   * Like Flutter's group.user_kicked event
   */
  async kickUser(conversationId, userId) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/kick`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to kick user",
      };
    }
    return { success: true, ...json };
  },

  /**
   * Add user to group (owner only)
   * POST /api/chat/conversations/{id}/add
   * Like Flutter's group.user_added event
   */
  async addUserToGroup(conversationId, userId) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/add`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to add user",
      };
    }
    return { success: true, ...json };
  },

  // ==================== Chat Summary (AI Feature - Flutter Sync) ====================

  /**
   * Get AI-generated chat summary
   * POST /api/chat/conversations/{id}/summary
   * Like Flutter's TalkJSRestService.getConversationSummary
   */
  async getChatSummary(conversationId, startDate, endDate) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/summary`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          start_time: startDate.toISOString(),
          end_time: endDate.toISOString(),
        }),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to generate summary",
      };
    }
    // Extract data from response structure: { success, message, data: { summary, message_count, ... } }
    const data = json?.data || {};
    return {
      success: true,
      summary: data.summary,
      message_count: data.message_count,
      metadata: data.metadata,
      start_time: data.start_time,
      end_time: data.end_time,
      message: json?.message,
    };
  },

  // ==================== Blocked Users List ====================

  /**
   * Get list of blocked users
   * GET /api/chat/friends/blocked
   */
  async getBlockedUsers() {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/friends/blocked`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await safeJson(res);
    return (json && json.data) || [];
  },

  // ==================== Friends List ====================

  /**
   * Get friends list (users with existing conversations)
   * GET /api/chat/friends
   * Like Flutter's AppEndpoint.chatFriends
   */
  async getFriendsList() {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/friends`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await safeJson(res);
    return (json && json.data) || [];
  },

  /**
   * Remove a friend (delete friendship)
   * DELETE /api/chat/friends/{conversationId}
   */
  async removeFriend(conversationId) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/friends/${conversationId}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to remove friend",
      };
    }
    return { success: true, ...json };
  },

  // ==================== Conversation Members ====================

  /**
   * Get conversation members
   * GET /api/chat/conversations/{conversationId}/members
   *
   * Returns members of a conversation with metadata.
   * Only participants can view members. Excludes blocked/kicked users.
   *
   * @param {string} conversationId - The TalkJS conversation ID (e.g., dm_188_332049, course_1942)
   * @returns {Promise<Object>} Response with success status and data/error
   *
   * Response structure:
   * {
   *   success: true,
   *   data: {
   *     conversation_id: string,
   *     type: 'direct' | 'group' | 'student_group',
   *     course_id: number | null,
   *     creator_id: string | null,
   *     admin_id: string | null,
   *     total_members: number,
   *     members: Array<{
   *       id: number,
   *       name: string,
   *       email: string,
   *       avatar: string,
   *       is_current_user: boolean,
   *       is_admin: boolean,
   *       access: string,
   *       notify: boolean
   *     }>
   *   }
   * }
   */
  async getConversationMembers(conversationId) {
    await ensureChatInitialized();

    if (!conversationId) {
      return {
        success: false,
        error: "Conversation ID is required",
        code: 400,
      };
    }

    try {
      const res = await fetch(
        `${apiUrl}/chat/conversations/${encodeURIComponent(
          conversationId
        )}/members`,
        {
          method: "GET",
          headers: {
            ...getAuthHeaders(),
            Accept: "application/json",
          },
        }
      );

      const json = await safeJson(res);

      // Handle specific error codes
      if (res.status === 403) {
        return {
          success: false,
          error: "User is not a participant in this conversation",
          code: 403,
        };
      }

      if (res.status === 404) {
        return {
          success: false,
          error: "Conversation not found",
          code: 404,
        };
      }

      if (res.status >= 500) {
        return {
          success: false,
          error: "Server error occurred",
          code: res.status,
        };
      }

      if (!res.ok) {
        return {
          success: false,
          error: json?.message || "Failed to fetch conversation members",
          code: res.status,
        };
      }

      // Extract data from response
      const data = json?.data || json;

      // Ensure the response has the expected structure
      const result = {
        success: true,
        data: {
          conversation_id: data.conversation_id || conversationId,
          type: data.type || "group",
          course_id: data.course_id || null,
          creator_id: data.creator_id || null,
          admin_id: data.admin_id || null,
          total_members: data.total_members || data.members?.length || 0,
          members: Array.isArray(data.members)
            ? data.members.map((member) => ({
                id: member.id,
                name: member.name || "",
                email: member.email || "",
                avatar: member.avatar || null,
                is_current_user: member.is_current_user || false,
                is_admin: member.is_admin || false,
                access: member.access || "ReadWrite",
                notify: member.notify !== false,
              }))
            : [],
        },
      };

      return result;
    } catch (err) {
      console.error("[ChatService] getConversationMembers error:", err);
      return {
        success: false,
        error: "Network error occurred while fetching members",
        code: 0,
      };
    }
  },

  // ==================== Message Search (Flutter Sync) ====================

  /**
   * Search messages across all conversations or within a specific conversation
   * GET /api/chat/messages/search
   * Like Flutter's TalkJSRestService.searchMessages
   *
   * @param {Object} params
   * @param {string} params.keyword - Search keyword (required)
   * @param {string} [params.conversationId] - Optional: limit search to specific conversation
   * @param {number} [params.limit=50] - Max results (1-100)
   * @returns {Promise<Object>} { success, keyword, total, results: Message[] }
   */
  async searchMessages({ keyword, conversationId, limit = 50 }) {
    await ensureChatInitialized();

    if (!keyword || keyword.trim().length === 0) {
      return { success: false, message: "Keyword is required", results: [] };
    }

    const queryParams = { keyword: keyword.trim(), limit };
    if (conversationId) {
      queryParams.conversation_id = conversationId;
    }

    const query = qs(queryParams);
    const res = await fetch(`${apiUrl}/chat/messages/search${query}`, {
      headers: getAuthHeaders(),
    });

    const json = await safeJson(res);

    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Search failed",
        statusCode: res.status,
        results: [],
      };
    }

    const data = json?.data || {};
    // Parse results - each result has { message, conversation_id, highlights }
    const results = (data.results || []).map((item) => ({
      ...item.message,
      _conversationId: item.conversation_id,
      _highlights: item.highlights || [],
    }));

    return {
      success: true,
      keyword: data.keyword || keyword,
      total: data.total || results.length,
      results,
    };
  },

  // ==================== Mute/Unmute Conversation (Flutter Sync) ====================

  /**
   * Mute a conversation
   * POST /api/chat/conversations/{id}/mute
   * Like Flutter's mute functionality
   *
   * @param {string} conversationId
   * @param {string} [duration] - 'always', '8h', '1w', or ISO timestamp
   */
  async muteConversation(conversationId, duration = "always") {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/mute`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ duration }),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to mute conversation",
      };
    }
    return { success: true, ...json };
  },

  /**
   * Unmute a conversation
   * POST /api/chat/conversations/{id}/unmute
   */
  async unmuteConversation(conversationId) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/unmute`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to unmute conversation",
      };
    }
    return { success: true, ...json };
  },

  /**
   * Get mute status for a conversation
   * POST /api/chat/conversations/{id}/mute
   */
  async getMuteStatus(conversationId) {
    await ensureChatInitialized();
    try {
      const res = await fetch(
        `${apiUrl}/chat/conversations/${conversationId}/mute`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );
      if (!res.ok) {
        // Endpoint not implemented yet, return default
        console.warn('[Chat] Mute status endpoint not available (404)');
        return { isMuted: false };
      }
      const json = await safeJson(res);
      return {
        isMuted: json?.data?.is_muted || false,
        mutedUntil: json?.data?.muted_until || null,
      };
    } catch (error) {
      console.warn('[Chat] Error fetching mute status:', error);
      return { isMuted: false };
    }
  },

  // ==================== User Online Status (Flutter Sync) ====================

  /**
   * Get user online status and friendship info
   * GET /api/chat/user-status/{userId}
   * Returns: is_online, conversation_id (null if not friends), shareable_id
   */
  async getUserStatus(userId) {
    await ensureChatInitialized();
    const res = await fetch(`${apiUrl}/chat/user-status/${userId}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return { is_online: false, conversation_id: null, shareable_id: null };
    const json = await safeJson(res);
    const data = json?.data || json || {};
    return {
      is_online: data.is_online || false,
      conversation_id: data.conversation_id || null,
      shareable_id: data.shareable_id || null,
      // Keep legacy fields for backward compatibility
      online: data.is_online || data.online || false,
      lastSeen: data.last_seen || null,
    };
  },

  // ==================== Upload with Cancel Support (Flutter Sync) ====================

  /**
   * Upload file with AbortController support for cancellation
   * Like Flutter's CancelToken
   *
   * @param {Object} params
   * @param {File} params.file
   * @param {string} params.filename
   * @param {string} [params.subtype]
   * @param {number} [params.width]
   * @param {number} [params.height]
   * @param {number} [params.duration]
   * @param {Function} [params.onProgress]
   * @param {AbortController} [params.abortController] - For cancellation
   * @returns {Promise<{token: string|null, xhr: XMLHttpRequest}>}
   */
  uploadFileWithCancel({
    file,
    filename,
    subtype,
    width,
    height,
    duration,
    onProgress,
    abortController,
  }) {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("file", file, filename || file.name || "file");
      form.append("filename", filename || file.name || "file");

      const validSubtypes = ["image", "video", "audio", "voice"];
      if (subtype && validSubtypes.includes(subtype)) {
        form.append("subtype", subtype);
      }
      if (width != null) form.append("width", String(width));
      if (height != null) form.append("height", String(height));
      if (duration != null) form.append("duration", String(duration));

      const xhr = new XMLHttpRequest();

      // Handle abort
      if (abortController) {
        abortController.signal.addEventListener("abort", () => {
          xhr.abort();
          reject(new Error("Upload cancelled"));
        });
      }

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const json = JSON.parse(xhr.responseText);
            const token =
              json &&
              (json.attachmentToken ||
                json.fileToken ||
                json.token ||
                json.data?.attachmentToken ||
                json.data?.fileToken);
            resolve({ token, xhr });
          } catch (e) {
            reject(new Error("Failed to parse upload response"));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"));
      });

      xhr.open("POST", `${apiUrl}/chat/files`);
      const headers = getAuthHeaders();
      Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === "content-type") return;
        xhr.setRequestHeader(key, headers[key]);
      });
      xhr.send(form);
    });
  },

  // ==================== Bulk Message Operations (Flutter Sync) ====================

  /**
   * Delete multiple messages at once
   * DELETE /api/chat/conversations/{id}/messages/bulk
   *
   * @param {string} conversationId
   * @param {string[]} messageIds
   */
  async deleteMessages(conversationId, messageIds) {
    await ensureChatInitialized();
    const res = await fetch(
      `${apiUrl}/chat/conversations/${conversationId}/messages/bulk`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
        body: JSON.stringify({ message_ids: messageIds }),
      }
    );
    const json = await safeJson(res);
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "Failed to delete messages",
      };
    }
    return { success: true, deleted: json?.data?.deleted || messageIds.length };
  },
};
